import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { ComboWizardData } from '../shared/combo-wizard-props'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    PROPOSITO_ALMA_NAME,
    PROPOSITO_ALMA_PREAMBLE,
    PROPOSITO_ALMA_STEPS,
    PROPOSITO_ALMA_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: ComboWizardData) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const buildRenacerPrompt = (cards: AllCardsType[], data: ComboWizardData): string => {
    const consultaSection =
        data.consulta.length > 0
            ? 'Al final de la interpretación, agrega una sección titulada "Foco en tu inquietud específica" donde respondas de forma concreta y definitiva a: ' +
              data.consulta
            : ''

    return `Por favor, realiza una interpretación de la Tirada del Renacer al Propósito - Gran Viaje del Alma de Tarot en un lenguaje sencillo de entender, pero hazla lo más detallada y extensa posible. Intenta ser lo más clara posible, para que esta interpretación le otorgue una visión clara, para ${data.consultante.name} de signo zodiacal ${data.consultante.zodiac}, teniendo en cuenta su signo zodiacal y su personalidad número ${data.consultante.personality} según la numerología. ${consultaSection} Organiza la tirada de la siguiente forma: 

1. El peso actual: (Lo Consciente) ${cardLabel(cards[0])} y (Lo Inconsciente) ${cardLabel(cards[1])}

2. Raíz profunda del vacío: (Origen Principal) ${cardLabel(cards[2])} y (Influencia Kármica o Energética) ${cardLabel(cards[3])}

3. El pasado que aún pesa: (Evento o Ciclo Clave) ${cardLabel(cards[4])} y (Lección no Integrada) ${cardLabel(cards[5])}

4. La sombra ignorada: (Aspecto Reprimido) ${cardLabel(cards[6])} y (Cómo se manifiesta hoy) ${cardLabel(cards[7])}

5. La chispa que sobrevive: (Fuerza o Deseo interno) ${cardLabel(cards[8])} y (Cómo nutrirla) ${cardLabel(cards[9])}

6. Recursos internos disponibles: (Fortaleza Principal) ${cardLabel(cards[10])} y (Recurso secundario) ${cardLabel(cards[11])}

7. El propósito que llama: (Propósito de esta etapa) ${cardLabel(cards[12])} y (Propósito mayor del Alma) ${cardLabel(cards[13])}

8. Apoyo externo y oportunidades: (Personas o situaciones aliadas) ${cardLabel(cards[14])} y (Oportunidad que se abrirá) ${cardLabel(cards[15])}

9. Qué soltar – Liberación de lo que ya no es para mí: (Hábito, creencia, relación o actividad que debes dejar ir) ${cardLabel(cards[16])} y (Alivio y ganancia energética al soltarlo) ${cardLabel(cards[17])}

10. El obstáculo principal: (Desafío actual) ${cardLabel(cards[18])} y (Cómo transformarlo en fuerza) ${cardLabel(cards[19])}

11. Pasos de apertura: (Acción inmediata – esta semana/mes) ${cardLabel(cards[20])} y (Acción a mediano plazo – 3 a 6 meses) ${cardLabel(cards[21])}

12. El renacer emocional: (Felicidad y ligereza que llegarán) ${cardLabel(cards[22])} y (Cómo se sentirá el alma al renacer) ${cardLabel(cards[23])}

13. Visión futura completa: (Resultado probable del camino) ${cardLabel(cards[24])} y (Mensaje protector del Universo) ${cardLabel(cards[25])}.

Dirígete directamente al consultante en segunda persona, con tono cálido, empático y sanador.`
}

const buildStepPrompt = (step: number, cards: AllCardsType[], data: ComboWizardData): string => {
    const extra = consultaExtra(data)

    if (step === 1) {
        return buildRenacerPrompt(cards, data)
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Temporada Actual del Alma** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a comprender qué estación o ciclo del alma está transitando en este momento de su vida, ${extra} según estas cartas: **Energía de la temporada actual (¿qué estación o energía del alma está transitando ahora?):** ${cardLabel(
            cards[0]
        )}, **Intensidad o duración aproximada (¿qué tan fuerte o cuánto tiempo puede durar esta temporada?):** ${cardLabel(
            cards[1]
        )}, **Propósito principal (¿para qué está esta temporada? ¿Qué quiere trabajar mi alma?):** ${cardLabel(
            cards[2]
        )}, **Lección secundaria / sombra (¿qué otra lección o aspecto sombra está presente?):** ${cardLabel(
            cards[3]
        )}, **Qué necesita ser soltado (¿qué está invitado/a a soltar en este ciclo?):** ${cardLabel(
            cards[4]
        )}, **Qué necesita ser cultivado (¿qué está invitado/a a nutrir o abrirse a recibir?):** ${cardLabel(
            cards[5]
        )}, **Influencias externas (¿qué factores externos están afectando esta temporada?):** ${cardLabel(
            cards[6]
        )}, **Resistencia o miedo (¿qué parte del consultante se está resistiendo o teniendo miedo?):** ${cardLabel(
            cards[7]
        )}, **El regalo/bendición que trae (¿cuál es el regalo oculto o la bendición de esta temporada?):** ${cardLabel(
            cards[8]
        )} y **Consejo del alma + transición (¿cómo transitarla con gracia? ¿qué temporada se asoma después?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el ciclo actual del alma: conecta la energía de la temporada, su propósito, las lecciones, lo que debe soltarse y cultivarse, y la transición hacia lo que viene. Detecta patrones, resistencias y bendiciones ocultas. Proporciona consejos prácticos y espirituales para transitar esta temporada con conciencia y gracia. Mantén un tono profesional, empático, profundo y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Conexión con tu Yo Futuro** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a conectar con la versión de sí mismo que puede llegar a ser en los próximos 1 a 3 años en clave de propósito y misión del alma, ${extra} según estas cartas: **Energía actual del consultante:** ${cardLabel(
        cards[0]
    )}, **Lo que más está pidiendo atención ahora:** ${cardLabel(cards[1])}, **Energía y presencia del Yo Futuro (quién se convierte esta persona dentro de 1 a 3 años; enfócate en su identidad, energía, presencia y cualidades principales en relación con su propósito):** ${cardLabel(
        cards[2]
    )}, **Cómo se siente emocionalmente esa versión futura:** ${cardLabel(cards[3])}, **Qué está creando o viviendo esa versión futura:** ${cardLabel(
        cards[4]
    )}, **Qué necesita soltar para llegar ahí:** ${cardLabel(cards[5])}, **Qué necesita cultivar o activar:** ${cardLabel(
        cards[6]
    )}, **Apoyo invisible o guías que ya tiene disponible:** ${cardLabel(cards[7])}, **El paso más poderoso y realista que puede dar ahora:** ${cardLabel(
        cards[8]
    )} y **Mensaje final de su Yo Futuro + visión del camino si sigue esta línea:** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Conecta el presente con el yo futuro en clave de propósito: muestra cómo la energía actual, lo que pide atención y lo que debe soltar o cultivar se relacionan con la identidad y el camino que revela el yo futuro. Detecta patrones, bloqueos y oportunidades en la lectura. Proporciona consejos prácticos, realistas y motivadores para acercarse a esa versión futura. Mantén un tono profesional, empático, esperanzador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: ComboWizardData,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = PROPOSITO_ALMA_STEPS[step - 1]
    const preamble = buildComboPreamble(
        PROPOSITO_ALMA_NAME,
        step,
        PROPOSITO_ALMA_TOTAL_STEPS,
        previousSteps,
        PROPOSITO_ALMA_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: PROPOSITO_ALMA_NAME,
        comboStep: step,
        comboTotalSteps: PROPOSITO_ALMA_TOTAL_STEPS,
    }
}
